/**
 * Envelope di errore delle API ecommerce (contratto del 09/09/2026).
 *
 * Gli errori applicativi 4xx NON usano lo status HTTP: arrivano con **HTTP 200**
 * e si riconoscono da `ok: false`, con lo status "vero" in
 * `error.original_status`. Solo i 5xx (e il 503 dell'email di conferma)
 * conservano lo status reale, usando lo stesso body.
 *
 * Conseguenza: `HttpClient` li considera risposte riuscite. È
 * `ecommerceApiErrorInterceptor` a convertirli in `ApiError` lanciati, così il
 * resto dell'applicazione continua a gestirli con try/catch.
 */
export interface ApiErrorFieldModel {
  path: string;
  code: string;
  message: string;
}

export interface ApiErrorDetailModel {
  code: string;
  message: string;
  original_status: number;
  fields?: ApiErrorFieldModel[];
}

export interface ApiErrorResponseModel {
  ok: false;
  error: ApiErrorDetailModel;
  /**
   * Alcuni errori portano anche il carrello ricalcolato, accanto a `error`:
   * `CART_REVIEW_REQUIRED` è il caso documentato. Resta `unknown` perché il
   * modello del carrello vive nel dominio ecommerce, non qui.
   */
  cart?: unknown;
}

/** Codici applicativi documentati, da preferire allo status HTTP. */
export const ApiErrorCodes = {
  REQUEST_VALIDATION_ERROR: 'REQUEST_VALIDATION_ERROR',
  CART_NOT_FOUND: 'CART_NOT_FOUND',
  CART_NOT_EDITABLE: 'CART_NOT_EDITABLE',
  CUSTOMER_REQUIRED: 'CUSTOMER_REQUIRED',
  CUSTOMER_EMAIL_AMBIGUOUS: 'CUSTOMER_EMAIL_AMBIGUOUS',
  ACCOUNT_EXISTS: 'ACCOUNT_EXISTS',
  ACCOUNT_DATA_REQUIRED: 'ACCOUNT_DATA_REQUIRED',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  VERIFICATION_TOKEN_INVALID: 'VERIFICATION_TOKEN_INVALID',
  PAYMENT_METHOD_UNAVAILABLE: 'PAYMENT_METHOD_UNAVAILABLE',
  PAYMENT_PROVIDER_NOT_IMPLEMENTED: 'PAYMENT_PROVIDER_NOT_IMPLEMENTED',
  ORDER_NOT_FOUND: 'ORDER_NOT_FOUND',
  ORDER_CONFIRMATION_EMAIL_UNAVAILABLE: 'ORDER_CONFIRMATION_EMAIL_UNAVAILABLE',
  /** Prova non prevista dal piano, oppure offerta illimitata non configurata. */
  NFARM_CONFIGURATION_INVALID: 'NFARM_CONFIGURATION_INVALID',
  /** Le condizioni della prova sono cambiate dopo il consenso: va chiesto di nuovo. */
  TRIAL_OFFER_CHANGED: 'TRIAL_OFFER_CHANGED',
  /** Coupon richiesto su un carrello senza configurazione. */
  NFARM_CONFIGURATION_REQUIRED: 'NFARM_CONFIGURATION_REQUIRED',
  // Motivi coupon (elenco completo del backend, 27/10/2026): arrivano sia come
  // `error.code` sia come `reason_code` del coupon e della sua validazione.
  COUPON_INVALID: 'COUPON_INVALID',
  COUPON_USED: 'COUPON_USED',
  COUPON_REVOKED: 'COUPON_REVOKED',
  COUPON_INACTIVE: 'COUPON_INACTIVE',
  COUPON_NOT_STARTED: 'COUPON_NOT_STARTED',
  COUPON_EXPIRED: 'COUPON_EXPIRED',
  COUPON_PLAN_NOT_ALLOWED: 'COUPON_PLAN_NOT_ALLOWED',
  /** Codice nominativo: serve il cliente sul carrello prima di applicarlo. */
  COUPON_CUSTOMER_REQUIRED: 'COUPON_CUSTOMER_REQUIRED',
  COUPON_CUSTOMER_MISMATCH: 'COUPON_CUSTOMER_MISMATCH',
  COUPON_NOT_SUPPORTED: 'COUPON_NOT_SUPPORTED',
  COUPON_CONFIGURATION_INVALID: 'COUPON_CONFIGURATION_INVALID',
  /**
   * Il riepilogo inviato col checkout non corrisponde più: nessun ordine
   * confermato, l'envelope porta il carrello aggiornato da rimostrare.
   */
  CART_REVIEW_REQUIRED: 'CART_REVIEW_REQUIRED',
} as const;

export type ApiErrorCode = (typeof ApiErrorCodes)[keyof typeof ApiErrorCodes];

/**
 * Errori che riguardano il coupon: al checkout tutti richiedono di rileggere
 * il carrello e di rimandare il cliente al riepilogo.
 */
export const COUPON_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  ApiErrorCodes.COUPON_INVALID,
  ApiErrorCodes.COUPON_USED,
  ApiErrorCodes.COUPON_REVOKED,
  ApiErrorCodes.COUPON_INACTIVE,
  ApiErrorCodes.COUPON_NOT_STARTED,
  ApiErrorCodes.COUPON_EXPIRED,
  ApiErrorCodes.COUPON_PLAN_NOT_ALLOWED,
  ApiErrorCodes.COUPON_CUSTOMER_REQUIRED,
  ApiErrorCodes.COUPON_CUSTOMER_MISMATCH,
  ApiErrorCodes.COUPON_NOT_SUPPORTED,
  ApiErrorCodes.COUPON_CONFIGURATION_INVALID,
]);

/** Errore applicativo ecommerce, lanciato al posto della risposta 200. */
export class ApiError extends Error {
  readonly code: string;
  readonly originalStatus: number;
  readonly fields: ApiErrorFieldModel[];
  /** Status HTTP reale della risposta: 200 per gli errori applicativi, 5xx per i guasti. */
  readonly httpStatus: number;
  /** Carrello allegato all'errore, se il server l'ha mandato. */
  readonly cart: unknown;

  constructor(detail: ApiErrorDetailModel, httpStatus: number, cart: unknown = null) {
    super(detail.message);
    this.name = 'ApiError';
    this.code = detail.code;
    this.originalStatus = detail.original_status;
    this.fields = detail.fields ?? [];
    this.httpStatus = httpStatus;
    this.cart = cart ?? null;
  }
}

/** Riconosce l'envelope `{ ok: false, error: {...} }` in un body qualsiasi. */
export function isApiErrorEnvelope(body: unknown): body is ApiErrorResponseModel {
  if (!body || typeof body !== 'object') {
    return false;
  }
  const candidate = body as { ok?: unknown; error?: { code?: unknown; message?: unknown } };
  return (
    candidate.ok === false &&
    !!candidate.error &&
    typeof candidate.error === 'object' &&
    typeof candidate.error.code === 'string'
  );
}
